import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, X, Send, Plus, Loader2, Maximize2, Minimize2 } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { axiosInstance } from "../App";

const COLORS = ["#ef4444", "#22d3ee", "#f59e0b", "#10b981", "#a78bfa", "#f472b6", "#38bdf8"];

const AiChart = ({ chart }) => {
  const { chart_type, title, labels = [], series = [] } = chart || {};
  const data = labels.map((label, i) => {
    const row = { name: label };
    series.forEach((s) => { row[s.name] = s.data?.[i] ?? 0; });
    return row;
  });
  if (!data.length || !series.length) return null;
  const common = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
      <XAxis dataKey="name" stroke="#999" tick={{ fontSize: 11 }} />
      <YAxis stroke="#999" tick={{ fontSize: 11 }} />
      <Tooltip contentStyle={{ background: "#171717", border: "1px solid #404040", color: "#fff" }} />
      <Legend wrapperStyle={{ fontSize: 11 }} />
    </>
  );
  return (
    <div className="bg-neutral-800/60 border border-neutral-700 rounded-lg p-3 my-2" data-testid="ai-chart">
      {title && <p className="text-neutral-200 text-xs font-semibold mb-2">{title}</p>}
      <ResponsiveContainer width="100%" height={220}>
        {chart_type === "pie" ? (
          <PieChart>
            <Tooltip contentStyle={{ background: "#171717", border: "1px solid #404040", color: "#fff" }} />
            <Pie data={data} dataKey={series[0].name} nameKey="name" outerRadius={80} label={{ fontSize: 10, fill: "#ccc" }}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
          </PieChart>
        ) : chart_type === "line" ? (
          <LineChart data={data}>{common}
            {series.map((s, i) => <Line key={s.name} type="monotone" dataKey={s.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />)}
          </LineChart>
        ) : chart_type === "area" ? (
          <AreaChart data={data}>{common}
            {series.map((s, i) => <Area key={s.name} type="monotone" dataKey={s.name} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.25} />)}
          </AreaChart>
        ) : (
          <BarChart data={data}>{common}
            {series.map((s, i) => <Bar key={s.name} dataKey={s.name} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />)}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const MessageBubble = ({ msg }) => (
  <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[88%] rounded-lg px-3 py-2 text-sm ${
      msg.role === "user" ? "bg-red-600/90 text-white" : "bg-neutral-800 text-neutral-100"
    }`}>
      {msg.role === "assistant" ? (
        <div className="ai-markdown prose prose-invert prose-sm max-w-none [&_table]:text-xs [&_th]:px-2 [&_td]:px-2 [&_td]:py-0.5 [&_th]:py-1 [&_table]:border-collapse [&_th]:border [&_td]:border [&_th]:border-neutral-600 [&_td]:border-neutral-700">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content || ""}</ReactMarkdown>
        </div>
      ) : (
        <p className="whitespace-pre-wrap">{msg.content}</p>
      )}
      {(msg.charts || []).map((c, i) => <AiChart key={i} chart={c} />)}
    </div>
  </div>
);

const SUGGESTIONS = [
  "Statut de la synchronisation ServiceCentre?",
  "Profit moyen des clients MSP en $ et %",
  "Graphique de la progression des revenus",
  "Quels clients perdent de l'argent?",
];

export const AiChatBubble = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [toolStatus, setToolStatus] = useState(null);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("ai_chat_session") || "");
  const bottomRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, toolStatus, open]);

  const loadHistory = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const res = await axiosInstance.get(`/ai/sessions/${sid}/messages`);
      setMessages(res.data.messages || []);
    } catch { /* session may not exist yet */ }
  }, []);

  useEffect(() => {
    if (open && sessionId && messages.length === 0) loadHistory(sessionId);
  }, [open, sessionId, loadHistory, messages.length]);

  const newConversation = () => {
    abortRef.current?.abort();
    setMessages([]);
    setSessionId("");
    localStorage.removeItem("ai_chat_session");
    setBusy(false);
    setToolStatus(null);
  };

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setToolStatus(null);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "", charts: [] }]);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const resp = await fetch(`${axiosInstance.defaults.baseURL}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: sessionId || undefined }),
        signal: controller.signal,
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let ev;
          try { ev = JSON.parse(line.slice(5)); } catch { continue; }
          if (ev.type === "session" && ev.session_id) {
            setSessionId(ev.session_id);
            localStorage.setItem("ai_chat_session", ev.session_id);
          } else if (ev.type === "delta") {
            setToolStatus(null);
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + ev.content };
              return copy;
            });
          } else if (ev.type === "tool") {
            setToolStatus(ev.name);
          } else if (ev.type === "chart") {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, charts: [...(last.charts || []), ev.chart] };
              return copy;
            });
          } else if (ev.type === "error") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: (copy[copy.length - 1].content || "") + `\n\n⚠️ Erreur: ${ev.message}` };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: "⚠️ Erreur de connexion à l'assistant. Réessayez." };
          return copy;
        });
      }
    } finally {
      setBusy(false);
      setToolStatus(null);
      setMessages((m) => {
        const last = m[m.length - 1];
        if (last && last.role === "assistant" && !(last.content || "").trim() && !(last.charts || []).length) {
          const copy = [...m];
          copy[copy.length - 1] = { ...last, content: "⚠️ La réponse a été interrompue avant d'arriver. Réessayez votre question — ou ouvrez une nouvelle conversation si ça persiste." };
          return copy;
        }
        return m;
      });
    }
  };

  const TOOL_LABELS = {
    get_sync_status: "Vérification des synchronisations…",
    get_msp_profitability: "Calcul de la rentabilité MSP…",
    get_revenue_summary: "Analyse des revenus…",
    get_client_details: "Recherche du client…",
    get_tickets_stats: "Analyse des billets…",
    get_contracts_overview: "Analyse des contrats…",
    make_chart: "Génération du graphique…",
    forecast: "Calcul des projections…",
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-red-600 hover:bg-red-500 shadow-lg shadow-red-900/40 flex items-center justify-center transition-transform hover:scale-105"
          data-testid="ai-chat-bubble"
          title="Assistant IA"
        >
          <Sparkles size={24} className="text-white" />
        </button>
      )}
      {open && (
        <div className={`fixed bottom-5 right-5 z-[60] bg-neutral-900 border border-neutral-700 rounded-xl shadow-2xl flex flex-col overflow-hidden transition-[width,height] duration-200 ${
          expanded
            ? "w-[1100px] max-w-[calc(100vw-2.5rem)] h-[calc(100vh-3rem)]"
            : "w-[420px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-4rem)]"
        }`} data-testid="ai-chat-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800 bg-neutral-950">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-red-500" />
              <h3 className="text-white text-sm font-semibold">Assistant IA</h3>
              <span className="text-neutral-500 text-[10px]">Claude Sonnet 5</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={newConversation} className="p-1.5 rounded hover:bg-neutral-800 text-neutral-400" title="Nouvelle conversation" data-testid="ai-chat-new">
                <Plus size={16} />
              </button>
              <button onClick={() => setExpanded((e) => !e)} className="p-1.5 rounded hover:bg-neutral-800 text-neutral-400" title={expanded ? "Réduire" : "Agrandir"} data-testid="ai-chat-expand">
                {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-neutral-800 text-neutral-400" data-testid="ai-chat-close">
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="ai-chat-messages">
            {messages.length === 0 && (
              <div className="text-center pt-8">
                <Sparkles size={28} className="text-red-500/60 mx-auto mb-2" />
                <p className="text-neutral-400 text-sm mb-4">Posez une question sur vos données:<br />sync, rentabilité, revenus, clients, billets, contrats…</p>
                <div className="space-y-1.5 px-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)}
                      className="w-full text-left text-xs text-neutral-300 bg-neutral-800/70 hover:bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 transition-colors"
                      data-testid="ai-chat-suggestion">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => <MessageBubble key={i} msg={msg} />)}
            {toolStatus && (
              <div className="flex items-center gap-2 text-neutral-400 text-xs pl-1" data-testid="ai-chat-tool-status">
                <Loader2 size={12} className="animate-spin" /> {TOOL_LABELS[toolStatus] || `Outil: ${toolStatus}…`}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-neutral-800 p-3 bg-neutral-950">
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                placeholder="Votre question…"
                disabled={busy}
                className="flex-1 bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white placeholder-neutral-500 focus:outline-none focus:border-red-600 disabled:opacity-60"
                data-testid="ai-chat-input"
              />
              <button
                onClick={() => send()}
                disabled={busy || !input.trim()}
                className="w-9 h-9 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-40 flex items-center justify-center shrink-0"
                data-testid="ai-chat-send"
              >
                {busy ? <Loader2 size={16} className="text-white animate-spin" /> : <Send size={16} className="text-white" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AiChatBubble;
